import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Calendar, Clock, ArrowUpRight } from "lucide-react";
import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";
import EmergencyBanner from "@/components/site/EmergencyBanner";
import { Reveal } from "@/components/site/Reveal";
import { Skeleton } from "@/components/ui/skeleton";
import { COMPANY } from "@/data/content";
import { setSeo } from "@/lib/seo";
import api from "@/lib/api";

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setSeo({
      title: `Actualités & conseils serrurerie — Lagny-sur-Marne • ${COMPANY.phoneDisplay}`,
      description: `☎ ${COMPANY.phoneDisplay} — Conseils de serrurier à Lagny-sur-Marne : porte claquée, cambriolage, choisir sa serrure, blindage. Guides pratiques par un artisan local en Seine-et-Marne (77).`,
      canonical: "https://serrurier-lagny-sur-marne.com/blog",
    });
    const stale = document.getElementById("ld-page");
    if (stale) stale.remove();
    window.scrollTo(0, 0);
    api
      .get("/blog")
      .then(({ data }) => setPosts(data))
      .catch(() => setPosts([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="App relative">
      <Navbar />
      <main>
        <section className="bg-brand-mist pt-32 pb-16">
          <div className="max-w-6xl mx-auto px-6 lg:px-12">
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-semibold text-brand-golddark">
              <span className="w-8 h-px bg-brand-gold" /> Actualités & conseils
            </span>
            <h1 className="mt-5 font-display font-semibold text-4xl md:text-6xl tracking-tight text-brand-navy leading-[1.05]">
              Le blog du serrurier
            </h1>
            <p className="mt-5 text-lg text-slate-600 max-w-2xl leading-relaxed">
              Nos conseils d&apos;artisan serrurier à Lagny-sur-Marne pour bien
              choisir votre serrure, éviter les mauvaises surprises et réagir
              aux urgences.
            </p>
          </div>
        </section>

        <section className="py-16 md:py-20 bg-white">
          <div
            className="max-w-6xl mx-auto px-6 lg:px-12 grid md:grid-cols-2 lg:grid-cols-3 gap-6"
            data-testid="blog-grid"
          >
            {loading &&
              [0, 1, 2].map((i) => (
                <div key={i} className="rounded-2xl border border-brand-line overflow-hidden">
                  <Skeleton className="aspect-[4/3] w-full" />
                  <div className="p-6 space-y-3">
                    <Skeleton className="h-3 w-1/2" />
                    <Skeleton className="h-5 w-full" />
                    <Skeleton className="h-4 w-3/4" />
                  </div>
                </div>
              ))}
            {!loading && posts.length === 0 && (
              <p className="text-slate-500 col-span-full" data-testid="blog-empty">
                Aucun article publié pour le moment.
              </p>
            )}
            {posts.map((p, i) => (
              <Reveal key={p.slug} delay={(i % 3) * 0.06}>
                <Link
                  to={`/blog/${p.slug}`}
                  data-testid={`blog-card-${p.slug}`}
                  className="group h-full flex flex-col rounded-2xl overflow-hidden border border-brand-line bg-white shadow-sm hover:shadow-xl hover:shadow-brand-navy/10 hover:-translate-y-1 transition-[box-shadow,transform] duration-300"
                >
                  <div className="aspect-[4/3] overflow-hidden">
                    <img
                      src={p.image}
                      alt={p.title}
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-6 flex flex-col flex-1">
                    <div className="flex items-center gap-4 text-xs uppercase tracking-[0.12em] text-slate-500 font-medium">
                      <span className="inline-flex items-center gap-1.5">
                        <Calendar className="w-3.5 h-3.5" /> {p.dateLabel}
                      </span>
                      <span className="inline-flex items-center gap-1.5">
                        <Clock className="w-3.5 h-3.5" /> {p.readingTime}
                      </span>
                    </div>
                    <h2 className="mt-3 font-display font-medium text-xl text-brand-navy leading-snug">
                      {p.title}
                    </h2>
                    <p className="mt-2 text-sm text-slate-600 leading-relaxed flex-1">
                      {p.excerpt}
                    </p>
                    <span className="mt-5 inline-flex items-center gap-1 text-sm font-semibold text-brand-golddark">
                      Lire l&apos;article{" "}
                      <ArrowUpRight className="w-4 h-4 group-hover:rotate-45 transition-transform duration-300" />
                    </span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </section>
      </main>
      <Footer />
      <EmergencyBanner />
    </div>
  );
}
